import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, api } from '@/lib/api';
import { formatTrn, useClients, useMe, type Client } from '@/lib/clients';
import { usePracticeOverview, type PracticeOverviewRow } from '@/lib/dashboard';
import { todayIso } from '@/lib/dates';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { TONE_CLASS, vatStatusChip } from '@/lib/vat';
import { GST_STATES, gstinProblem, stateLabel } from '@/lib/gst';
import { useOrgId } from '@/lib/org';
import { useOpenClient } from '@/lib/use-open-client';

export const Route = createFileRoute('/practice/clients')({ component: ClientsPage });

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const EMIRATES = [
  'Abu Dhabi',
  'Dubai',
  'Sharjah',
  'Ajman',
  'Umm Al Quwain',
  'Ras Al Khaimah',
  'Fujairah',
];
const TEMPLATES: Array<{ value: string; label: string }> = [
  { value: 'trading', label: 'Trading (goods)' },
  { value: 'services', label: 'Services' },
  { value: 'free_zone', label: 'Free zone company' },
  { value: 'india_trading', label: 'Trading (goods), India' },
  { value: 'india_services', label: 'Services, India' },
];

/** Screen P1 (document 06): My practice › Clients, list pattern. */
function ClientsPage() {
  const clients = useClients();
  const overview = usePracticeOverview();
  const status = new Map((overview.data ?? []).map((o) => [o.orgId, o]));
  const today = todayIso();
  const me = useMe();
  const orgId = useOrgId();
  const openClient = useOpenClient();
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  const needle = q.trim().toLowerCase().replace(/\s+/g, '');
  const rows = (clients.data ?? []).filter(
    (c) =>
      !needle ||
      c.name.toLowerCase().replace(/\s+/g, '').includes(needle) ||
      (c.trn ?? '').includes(needle) ||
      (c.gstin ?? '').toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        title="Clients"
        description="Every client whose books you can open."
        actions={
          me.data?.isStaff ? <Button onClick={() => setAdding(true)}>Add client</Button> : null
        }
      />
      <div className="mb-3 flex items-center gap-3">
        <div className="relative w-[320px]">
          <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
          <Input
            className="bg-card pl-8"
            placeholder="Search by name or tax number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search clients"
          />
        </div>
        {clients.data ? (
          <span className="text-sm text-muted-foreground">
            {rows.length} of {clients.data.length} clients
          </span>
        ) : null}
      </div>
      {clients.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : clients.isError ? (
        <EmptyState title="Could not load clients" hint={clients.error.message} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={clients.data.length === 0 ? 'No clients yet' : 'No clients match your search'}
          hint={
            clients.data.length === 0
              ? 'Add the first client to start keeping its books.'
              : undefined
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Tax number</TableHead>
                <TableHead>Tax period</TableHead>
                <TableHead className="text-right">Bank items to review</TableHead>
                <TableHead className="text-right">Unreconciled accounts</TableHead>
                <TableHead>Last locked period</TableHead>
                <TableHead>Accountants</TableHead>
                <TableHead className="w-[140px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} className={c.id === orgId ? 'bg-accent/60' : ''}>
                  <TableCell className="font-medium">
                    <button
                      type="button"
                      className="text-left hover:text-primary hover:underline"
                      onClick={() => openClient(c.id, { toDashboard: true })}
                    >
                      {c.name}
                    </button>
                    {c.id === orgId ? (
                      <Badge variant="secondary" className="ml-2">
                        Open
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="tnum">
                    {c.taxPack === 'in' ? (c.gstin ?? '') : formatTrn(c.trn)}
                  </TableCell>
                  <ComplianceCells
                    row={status.get(c.id)}
                    loading={overview.isPending}
                    today={today}
                    taxPeriod={c.taxPeriod}
                    india={c.taxPack === 'in'}
                  />
                  <TableCell className="max-w-[180px] truncate">
                    {c.accountants.join(', ')}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openClient(c.id, { toDashboard: true })}
                    >
                      Open books
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <AddClientDrawer open={adding} onOpenChange={setAdding} />
    </>
  );
}

/** Tax return status, bank items, unreconciled accounts and lock date of one client (P1). */
function ComplianceCells({
  row,
  loading,
  today,
  taxPeriod,
  india,
}: {
  row: PracticeOverviewRow | undefined;
  loading: boolean;
  today: string;
  taxPeriod: string;
  india: boolean;
}) {
  if (!row)
    return (
      <>
        <TableCell colSpan={4} className="text-xs text-muted-foreground">
          {loading ? 'Loading…' : ''}
        </TableCell>
      </>
    );
  if (row.error)
    return (
      <TableCell colSpan={4} className="text-xs text-destructive" title={row.error}>
        Could not read this client’s status
      </TableCell>
    );
  const chip = row.vat ? vatStatusChip(row.vat, today) : null;
  return (
    <>
      <TableCell>
        {!row.vatRegistered ? (
          <span className="text-xs text-muted-foreground">
            Not registered for {india ? 'GST' : 'VAT'}
          </span>
        ) : row.vat && chip ? (
          <div className="flex flex-col items-start gap-0.5">
            <span
              className={cn('rounded-full border px-2 text-xs', TONE_CLASS[chip.tone])}
              data-testid="vat-chip"
            >
              {chip.label}
            </span>
            <span className="text-xs text-muted-foreground">
              {row.vat.label} · due {formatDate(row.vat.dueDate)}
              {row.vat.overdueReturns > 1 ? ` · ${row.vat.overdueReturns} overdue` : ''}
            </span>
          </div>
        ) : (
          <span className="text-xs capitalize text-muted-foreground">{taxPeriod} · Filed</span>
        )}
      </TableCell>
      <TableCell className="tnum text-right">{row.bankItemsToReview || ''}</TableCell>
      <TableCell className="tnum text-right">{row.unreconciledAccounts || ''}</TableCell>
      <TableCell>{row.lastLockedPeriod ? formatDate(row.lastLockedPeriod) : 'None'}</TableCell>
    </>
  );
}

interface ClientForm {
  name: string;
  legalName: string;
  /** IN (India GST, the default) or AE (UAE VAT). */
  country: string;
  gstin: string;
  stateCode: string;
  emirate: string;
  trn: string;
  fiscalYearStart: string;
  taxPeriod: string;
  chartTemplate: string;
}

const EMPTY: ClientForm = {
  name: '',
  legalName: '',
  country: 'IN',
  gstin: '',
  stateCode: '27',
  emirate: 'Dubai',
  trn: '',
  fiscalYearStart: '4',
  taxPeriod: 'monthly',
  chartTemplate: 'india_trading',
};

/** What the country decides: home currency, tax, financial year, return period and chart. */
const COUNTRY_DEFAULTS: Record<
  string,
  { currency: string; tax: string; taxNumber: string; fy: string; period: string; chart: string }
> = {
  IN: {
    currency: 'INR – Indian rupee',
    tax: 'GST',
    taxNumber: 'GSTIN',
    fy: '4',
    period: 'monthly',
    chart: 'india_trading',
  },
  AE: {
    currency: 'AED – UAE dirham',
    tax: 'VAT',
    taxNumber: 'TRN',
    fy: '1',
    period: 'quarterly',
    chart: 'trading',
  },
};

function AddClientDrawer({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const openClient = useOpenClient();
  const [form, setForm] = useState<ClientForm>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof ClientForm, string>>>({});
  const set = (k: keyof ClientForm) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const india = form.country === 'IN';
  const defaults = COUNTRY_DEFAULTS[form.country]!;
  /** The country sets the currency, tax, financial year, return period and chart. */
  const setCountry = (country: string) => {
    const d = COUNTRY_DEFAULTS[country]!;
    setForm((f) => ({
      ...f,
      country,
      fiscalYearStart: d.fy,
      taxPeriod: d.period,
      chartTemplate: d.chart,
    }));
    setErrors({});
  };

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Client>('/practice/clients', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async (client) => {
      await qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
      toast(`${client.name} added.`);
      setForm(EMPTY);
      onOpenChange(false);
      openClient(client.id, { toDashboard: true });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const trn = form.trn.replace(/\s+/g, '');
    const next: typeof errors = {};
    if (form.name.trim().length < 2) next.name = 'Enter the client name.';
    const gstin = form.gstin.replace(/\s+/g, '').toUpperCase();
    if (!india && trn && !/^[0-9]{15}$/.test(trn)) next.trn = 'A TRN has 15 digits.';
    if (india && gstin) {
      const problem = gstinProblem(gstin);
      if (problem) next.gstin = problem;
      else if (gstin.slice(0, 2) !== form.stateCode)
        next.stateCode = `The GSTIN is registered in ${stateLabel(gstin.slice(0, 2))}.`;
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    create.mutate({
      name: form.name.trim(),
      ...(form.legalName.trim() ? { legalName: form.legalName.trim() } : {}),
      country: form.country,
      ...(india
        ? { stateCode: form.stateCode, ...(gstin ? { gstin } : {}) }
        : { ...(form.emirate ? { emirate: form.emirate } : {}), ...(trn ? { trn } : {}) }),
      fiscalYearStart: Number(form.fiscalYearStart),
      taxPeriod: form.taxPeriod,
      chartTemplate: form.chartTemplate,
    });
  }

  const serverError =
    create.error instanceof ApiError
      ? create.error.message
      : create.error
        ? String(create.error)
        : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <form onSubmit={submit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>Add client</SheetTitle>
            <SheetDescription>
              Sets up the books: financial years, tax rates for the client's country and a chart of
              accounts from the template.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <ErrorBanner>{serverError}</ErrorBanner>
            <Field
              label="Country"
              hint="Sets the home currency, tax, financial year and tax number."
            >
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.country}
                  onChange={(e) => setCountry(e.target.value)}
                  autoFocus
                >
                  <option value="IN">India</option>
                  <option value="AE">United Arab Emirates</option>
                </NativeSelect>
              )}
            </Field>
            <Field label="Client name" error={errors.name}>
              {(id) => (
                <Input
                  id={id}
                  value={form.name}
                  onChange={(e) => set('name')(e.target.value)}
                  aria-invalid={Boolean(errors.name)}
                />
              )}
            </Field>
            <Field
              label="Legal name"
              hint={`As registered${india ? ' (certificate of incorporation or GST registration)' : ' (trade licence)'}. Defaults to the client name.`}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.legalName}
                  onChange={(e) => set('legalName')(e.target.value)}
                />
              )}
            </Field>
            <div className="grid grid-cols-1 gap-3">
              {india ? (
                <Field label="State" error={errors.stateCode}>
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.stateCode}
                      onChange={(e) => set('stateCode')(e.target.value)}
                    >
                      {GST_STATES.map(([code, name]) => (
                        <option key={code} value={code}>
                          {code} · {name}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
              ) : (
                <Field label="Emirate">
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.emirate}
                      onChange={(e) => set('emirate')(e.target.value)}
                    >
                      {EMIRATES.map((em) => (
                        <option key={em}>{em}</option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
              )}
            </div>
            {india ? (
              <Field
                label="GSTIN"
                hint="15 characters; leave blank if not registered for GST."
                error={errors.gstin}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={form.gstin}
                    placeholder="27AAGCG4321K1Z5"
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      set('gstin')(v);
                      const code = v.replace(/\s+/g, '').slice(0, 2);
                      if (/^\d{2}$/.test(code) && GST_STATES.some(([c]) => c === code))
                        set('stateCode')(code);
                    }}
                    aria-invalid={Boolean(errors.gstin)}
                  />
                )}
              </Field>
            ) : (
              <Field
                label="TRN"
                hint="15 digits; leave blank if not VAT registered."
                error={errors.trn}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={form.trn}
                    inputMode="numeric"
                    placeholder="100 1234567 00 003"
                    onChange={(e) => set('trn')(e.target.value)}
                    aria-invalid={Boolean(errors.trn)}
                  />
                )}
              </Field>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Home currency">
                {(id) => <Input id={id} value={defaults.currency} disabled />}
              </Field>
              <Field label="First month of financial year">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.fiscalYearStart}
                    onChange={(e) => set('fiscalYearStart')(e.target.value)}
                  >
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            </div>
            <Field label={`${defaults.tax} return period`}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.taxPeriod}
                  onChange={(e) => set('taxPeriod')(e.target.value)}
                >
                  <option value="quarterly">Quarterly</option>
                  <option value="monthly">Monthly</option>
                </NativeSelect>
              )}
            </Field>
            <Field label="Chart of accounts template">
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.chartTemplate}
                  onChange={(e) => set('chartTemplate')(e.target.value)}
                >
                  {TEMPLATES.filter((t) => t.value.startsWith('india_') === india).map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          <SheetFooter className="flex-row justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Saving…' : 'Save'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
